import type { CarriedUnit, TurnWorld, Unit } from './types';
import { UNIT_DEFS, resolveBaseType } from './units';
import { canTraverse } from './terrain';
import { validateCaptures } from './capture';
import { validateBuilds } from './build';

/** Check if a unit can load into a carrier. */
export function canLoadInto(unit: Unit, carrier: Unit): boolean {
  if (unit.owner !== carrier.owner) return false;
  const carrierDef = UNIT_DEFS[carrier.type];
  if (!carrierDef.cargoCapacity || !carrierDef.cargoAccepts) return false;
  // Accept the unit type directly or via its base type (faction-unique units)
  if (!carrierDef.cargoAccepts.includes(unit.type) && !carrierDef.cargoAccepts.includes(resolveBaseType(unit.type))) return false;
  if ((carrier.cargo?.length ?? 0) >= carrierDef.cargoCapacity) return false;
  return true;
}

/** Load a unit into a carrier. Removes unit from world and resets captures.
 *  The loaded unit loses all movement points (marked as acted). */
export function executeLoad(world: TurnWorld, unit: Unit, carrier: Unit): void {
  const carried: CarriedUnit = {
    type: unit.type,
    hp: unit.hp,
    owner: unit.owner,
    acted: true, // loading consumes all movement — can't unload same turn
    xp: unit.xp,
  };
  if (!carrier.cargo) carrier.cargo = [];
  carrier.cargo.push(carried);
  world.units.delete(unit.id);
  validateCaptures(world);
  validateBuilds(world);
}

/** Check if a carrier can unload (has cargo and at least one free adjacent land tile). */
export function canUnload(world: TurnWorld, carrier: Unit): boolean {
  if (!carrier.cargo || carrier.cargo.length === 0) return false;
  return getUnloadTiles(world, carrier).length > 0;
}

/** Get adjacent tiles where a carrier can unload cargo. */
export function getUnloadTiles(world: TurnWorld, carrier: Unit, cargoIndex = 0): { col: number; row: number }[] {
  // 8-directional: cargo can be dropped on any adjacent tile, diagonals included.
  const directions = [[0, -1], [0, 1], [-1, 0], [1, 0], [-1, -1], [1, -1], [-1, 1], [1, 1]];
  const tiles: { col: number; row: number }[] = [];

  // Validate candidate tiles against the DISEMBARKING unit's own domain, not a
  // hardcoded land/infantry. A carrier on open water can launch planes (air)
  // onto adjacent water tiles; land cargo still needs land/coast it can stand on.
  const cargoUnit = carrier.cargo?.[cargoIndex];
  const cargoDomain = cargoUnit ? UNIT_DEFS[cargoUnit.type].domain : 'land';
  const cargoType = cargoUnit?.type ?? 'infantry';

  for (const [dc, dr] of directions) {
    const nc = carrier.col + dc;
    const nr = carrier.row + dr;
    if (nc < 0 || nr < 0 || nc >= world.map.width || nr >= world.map.height) continue;

    // Must be passable for the disembarking unit's domain
    const terrain = world.map.tiles[nr][nc].type;
    if (!canTraverse(terrain, cargoDomain, cargoType)) continue;

    // Must be free of other units
    let occupied = false;
    for (const [, u] of world.units) {
      if (u.col === nc && u.row === nr) { occupied = true; break; }
    }
    if (occupied) continue;

    tiles.push({ col: nc, row: nr });
  }
  return tiles;
}

/** Unload a carried unit from carrier onto a target tile. */
export function executeUnload(
  world: TurnWorld,
  carrier: Unit,
  targetCol: number,
  targetRow: number,
  cargoIndex: number,
): void {
  if (!carrier.cargo || cargoIndex < 0 || cargoIndex >= carrier.cargo.length) return;

  const carried = carrier.cargo[cargoIndex];
  carrier.cargo.splice(cargoIndex, 1);

  const def = UNIT_DEFS[carried.type];
  // Aircraft "launch" from a carrier with a full turn (catapult takeoff) — fuelled,
  // armed, and free to fly a sortie the same turn. Ground/naval cargo disembarks
  // "spent" (acted) like an APC drop. The carrier is limited to one unload per turn
  // by the caller (unloading ends its turn), so at most one plane launches per turn.
  const launches = def.domain === 'air';
  const unit: Unit = {
    id: world.nextId++,
    type: carried.type,
    owner: carried.owner,
    col: targetCol,
    row: targetRow,
    hp: carried.hp,
    acted: !launches,
    movePoints: launches ? def.movementRange : 0,
    fuel: def.maxFuel,
    ammo: def.maxAmmo,
    xp: carried.xp,
  };
  world.units.set(unit.id, unit);
}
